import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { gbp } from "@/lib/stages";
import { X, Maximize2, PoundSterling } from "lucide-react";

const REFRESH_MS = 45000;
const BAR = ["#0055FF", "#2DD4BF", "#7DA9FF", "#0B6B4F", "#F59E0B", "#A78BFA"];

/** The floor board — built for a screen on the wall. Cases and targets always; each agent's own
    commission share too, if the holder has switched money on. Client loan amounts never. */
export default function Board() {
  const [d, setD] = useState(null);
  const [clock, setClock] = useState(() => new Date());
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get("/refer/board").then((r) => setD(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const a = setInterval(load, REFRESH_MS);
    const b = setInterval(() => setClock(new Date()), 30000);
    return () => { clearInterval(a); clearInterval(b); };
  }, [load]);

  const toggleMoney = async () => {
    if (!d) return;
    setBusy(true);
    try {
      const r = await api.patch("/refer/board/settings", { money: !d.money });
      setD((s) => ({ ...s, money: r.data.money }));
      load();
    } catch { /* the board stays as it was */ }
    finally { setBusy(false); }
  };

  const full = () => {
    const el = document.documentElement;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el.requestFullscreen?.();
  };

  const money = !!d?.money;
  const rows = (d?.rows || []).filter((r) => !r.you || r.this_month > 0 || r.monthly_target);
  const leader = rows.find((r) => r.this_month > 0);
  const cols = money
    ? "56px minmax(0,1.3fr) minmax(0,1.7fr) 84px 84px 132px"
    : "56px minmax(0,1.4fr) minmax(0,2fr) 92px 92px";

  return (
    <div data-testid="floor-board" style={{
      minHeight: "100vh", background: "radial-gradient(1200px 600px at 15% -10%, #11203d 0%, #080d18 55%, #05080f 100%)",
      color: "#F8FAFC", fontFamily: "'Instrument Sans', system-ui, sans-serif", padding: "clamp(20px, 3vw, 46px)",
    }}>
      <style>{`
        @keyframes bfill { from { width: 0 } }
        .bf { animation: bfill 1.1s cubic-bezier(.2,.8,.2,1) both }
        .brow { transition: background-color .3s ease }
        .brow:hover { background: rgba(125,169,255,.06) }
      `}</style>

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, letterSpacing: ".3em", textTransform: "uppercase", color: "#7DA9FF", fontWeight: 700 }}>
            {d?.company || "Your floor"} · {d?.month_label || ""}
          </div>
          <h1 style={{ margin: "10px 0 0", font: "800 clamp(34px, 5vw, 64px)/1 Archivo, sans-serif", letterSpacing: "-.03em" }}>
            The board
          </h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ textAlign: "right", marginRight: 6 }}>
            <div style={{ fontVariantNumeric: "tabular-nums", font: "700 clamp(20px,2.4vw,30px)/1 'Instrument Sans'" }}>
              {clock.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </div>
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>updates every 45s</div>
          </div>
          <button onClick={toggleMoney} data-testid="board-money-toggle" disabled={busy}
                  title={money ? "Take the agents' shares off the board" : "Show each agent their own share"}
                  style={{ ...btn, width: "auto", padding: "0 14px", gap: 8, gridAutoFlow: "column",
                           display: "inline-grid", alignItems: "center",
                           borderColor: money ? "rgba(45,212,191,.45)" : "rgba(125,169,255,.22)",
                           background: money ? "rgba(45,212,191,.12)" : "rgba(125,169,255,.07)",
                           color: money ? "#2DD4BF" : "#CBD5E1" }}>
            <PoundSterling size={14} />
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".04em" }}>{money ? "Shares on" : "Shares off"}</span>
          </button>
          <button onClick={full} data-testid="board-fullscreen" title="Full screen"
                  style={btn}><Maximize2 size={16} /></button>
          <button onClick={() => nav("/introducer")} data-testid="board-exit" title="Back to your floor"
                  style={btn}><X size={16} /></button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14, margin: "clamp(22px,3vw,40px) 0" }}
           data-testid="board-stats">
        <Stat k="Cases this month" v={d?.month_cases ?? "—"} />
        <Stat k="Funded this month" v={d?.month_funded ?? "—"} tone="#2DD4BF" />
        <Stat k="Live now" v={d?.live ?? "—"} />
        <Stat k="Floor target" v={d?.target ?? "—"} sub={d?.target ? `${Math.min(100, Math.round(100 * (d.month_cases || 0) / d.target))}% there` : "not set"} />
        {money ? <Stat k="Agent shares" v={gbp(d?.month_earned_gbp || 0)} tone="#2DD4BF" sub="earned by the floor this month" /> : null}
      </div>

      <div style={{ border: "1px solid rgba(125,169,255,.14)", borderRadius: 18, overflow: "hidden", background: "rgba(8,14,26,.6)" }}>
        {rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#64748B", fontSize: 16 }} data-testid="board-empty">
            No agents on seats yet. Add them from your floor and this board fills itself.
          </div>
        ) : rows.map((r, i) => {
          const pct = r.monthly_target ? Math.min(100, Math.round(100 * r.this_month / r.monthly_target)) : null;
          const hit = pct === 100;
          const top = leader && r.id === leader.id;
          return (
            <div key={r.id} className="brow" data-testid={`board-row-${r.id}`}
                 style={{ display: "grid", gridTemplateColumns: cols,
                          alignItems: "center", gap: "clamp(10px,1.4vw,22px)", padding: "clamp(14px,1.7vw,22px) clamp(16px,2vw,26px)",
                          borderTop: i ? "1px solid rgba(125,169,255,.10)" : "none",
                          background: top ? "linear-gradient(90deg, rgba(0,85,255,.16), rgba(0,85,255,0) 60%)" : "transparent" }}>
              <div style={{ fontVariantNumeric: "tabular-nums", font: "800 clamp(20px,2.2vw,30px)/1 Archivo",
                            color: top ? "#7DA9FF" : "#334155" }}>{i + 1}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ font: "700 clamp(17px,1.9vw,26px)/1.15 'Instrument Sans'", overflow: "hidden",
                              textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.name}{r.you ? <span style={{ color: "#64748B", fontWeight: 500 }}> · the desk</span> : null}
                </div>
                <div style={{ fontSize: "clamp(11px,1.1vw,13px)", color: top ? "#7DA9FF" : "#64748B", marginTop: 5, fontWeight: 600,
                              letterSpacing: ".04em", textTransform: "uppercase" }}>
                  {top ? "Top this month" : r.status === "paused" ? "Paused" : hit ? "Target hit" : r.this_month === 0 ? "Nothing yet" : `${r.live} live`}
                </div>
              </div>
              <div>
                <div style={{ height: 14, borderRadius: 999, background: "rgba(148,163,184,.14)", overflow: "hidden" }}>
                  <div className="bf" style={{ height: "100%", width: `${pct ?? Math.min(100, r.this_month * 12)}%`,
                                               borderRadius: 999, animationDelay: `${i * 90}ms`,
                                               background: hit ? "linear-gradient(90deg,#0B6B4F,#2DD4BF)" : `linear-gradient(90deg,${BAR[i % BAR.length]},#7DA9FF)` }} />
                </div>
                <div style={{ fontSize: "clamp(10.5px,1vw,12.5px)", color: "#64748B", marginTop: 7 }}>
                  {r.monthly_target ? `${r.this_month} of ${r.monthly_target} this month` : "no target set"}
                </div>
              </div>
              <Num v={r.this_month} k="in" />
              <Num v={r.funded_month} k="funded" tone={r.funded_month ? "#2DD4BF" : undefined} />
              {money ? (
                <div style={{ textAlign: "right" }} data-testid={`board-earned-${r.id}`}>
                  <div style={{ fontVariantNumeric: "tabular-nums", font: "800 clamp(17px,1.9vw,26px)/1 Archivo",
                                color: r.earned_gbp ? "#2DD4BF" : "#334155" }}>
                    {r.you ? "—" : gbp(r.earned_gbp || 0)}
                  </div>
                  <div style={{ fontSize: 10.5, letterSpacing: ".14em", textTransform: "uppercase", color: "#64748B",
                                marginTop: 5, fontWeight: 700 }}>
                    {r.you ? "the company" : "your share"}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 18, fontSize: 12, color: "#475569" }} data-testid="board-money-note">
        {money
          ? "Each agent's own share of this month's funded cases. Client loan amounts are never shown — and remember this is one screen, so everyone on the floor reads everyone's figures."
          : "Cases and targets only. Turn Shares on to put each agent's own earnings up beside their name."}
      </div>
    </div>
  );
}

const btn = {
  width: 40, height: 40, display: "grid", placeItems: "center", borderRadius: 12, cursor: "pointer",
  border: "1px solid rgba(125,169,255,.22)", background: "rgba(125,169,255,.07)", color: "#CBD5E1",
};

const Stat = ({ k, v, sub, tone }) => (
  <div style={{ border: "1px solid rgba(125,169,255,.14)", borderRadius: 16, padding: "clamp(14px,1.6vw,20px)",
                background: "rgba(8,14,26,.55)" }}>
    <div style={{ fontSize: 10.5, letterSpacing: ".16em", textTransform: "uppercase", color: "#64748B", fontWeight: 700 }}>{k}</div>
    <div style={{ marginTop: 8, fontVariantNumeric: "tabular-nums", font: "800 clamp(28px,3.4vw,44px)/1 Archivo",
                  color: tone || "#F8FAFC" }}>{v}</div>
    {sub ? <div style={{ marginTop: 6, fontSize: 12, color: "#64748B" }}>{sub}</div> : null}
  </div>
);

const Num = ({ v, k, tone }) => (
  <div style={{ textAlign: "right" }}>
    <div style={{ fontVariantNumeric: "tabular-nums", font: "800 clamp(20px,2.3vw,32px)/1 Archivo", color: tone || "#F8FAFC" }}>{v}</div>
    <div style={{ fontSize: 10.5, letterSpacing: ".14em", textTransform: "uppercase", color: "#64748B", marginTop: 5, fontWeight: 700 }}>{k}</div>
  </div>
);
